import { useState, useCallback, useEffect } from 'react';
import useApiCall from '../../services/api/useApiCall';
import { buildApiConfig } from '../../services/api/apiClient';
import { endPointEnums } from '../../services/api/endpoints';

export default function useWallet(userId) {
  const [wallet, setWallet] = useState({ available: 0, blocked: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { makeApiCall } = useApiCall();

  const fetchWallet = useCallback(async () => {
    if (!userId) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const apiConfig = buildApiConfig(endPointEnums.WALLET_BALANCE, {
        userId,
        _t: Date.now(), // Cache buster to ensure local balance refresh
      });
      const res = await makeApiCall(apiConfig);

      const balance = res?.data?.balance || res?.balance || {};
      setWallet({
        available: balance.available ?? 0,
        blocked: balance.blocked ?? 0,
      });
    } catch (err) {
      console.error('useWallet: Error fetching wallet:', err);
      setError('Failed to fetch wallet balance');
    } finally {
      setLoading(false);
    }
  }, [userId, makeApiCall]);

  // Auto-fetch on mount and when userId changes
  useEffect(() => {
    if (userId) {
      fetchWallet();
    }
  }, [userId, fetchWallet]);

  // Refresh when tab regains visibility (e.g. user returns after Razorpay redirect)
  useEffect(() => {
    if (!userId) return;

    let visibilityTimeout;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Add a small delay to prevent rapid-fire calls on tab flickers
        clearTimeout(visibilityTimeout);
        visibilityTimeout = setTimeout(() => {
          fetchWallet();
        }, 500);
      }
    };

    // Listen for programmatic wallet-updated events dispatched from usePaymentActions
    const handleWalletUpdated = () => {
      fetchWallet();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('walletUpdated', handleWalletUpdated);

    return () => {
      clearTimeout(visibilityTimeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('walletUpdated', handleWalletUpdated);
    };
  }, [userId, fetchWallet]);

  return { wallet, loading, error, fetchWallet };
}
